import React, { useState, useCallback } from 'react';
import ConfirmationModal from '../components/forms/ConfirmationModal';
import { UX_TIMING, OPERATION_STATUS } from '../constants';

/**
 * Hook reutilizable para gestionar operaciones CRUD y sus modales asociados (Form, Delete, Operation Status).
 * @param {object} config - Configuración para el hook.
 * @param {string} config.entityName - Nombre de la entidad (ej. 'Contacto', 'Usuario').
 * @param {string} config.entityNamePlural - Nombre de la entidad en plural (ej. 'Contactos', 'Usuarios').
 * @param {object} config.crudFunctions - Objeto con las funciones create, update, delete del hook de la entidad (ej. useContacts).
 * @param {React.Component} config.FormModalComponent - El componente de modal de formulario a renderizar.
 * @param {object} config.formModalProps - Props adicionales para pasar al FormModalComponent.
 * @param {string} config.entityIdKey - La clave del ID de la entidad (por defecto 'id').
 */
const usePageOperations = ({
  entityName,
  entityNamePlural,
  crudFunctions,
  FormModalComponent,
  formModalProps = {},
  entityIdKey = 'id',
}) => {
  // --- ESTADOS ---
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isOperationModalOpen, setIsOperationModalOpen] = useState(false);
  
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [operationStatus, setOperationStatus] = useState(OPERATION_STATUS.IDLE);
  const [operationError, setOperationError] = useState(null);
  const [currentOperationType, setCurrentOperationType] = useState(null); // 'create', 'update', 'delete'

  const isEditing = Boolean(selectedEntity && selectedEntity[entityIdKey]);

  // --- HANDLERS DE MODALES ---
  const openCreateModal = useCallback(() => {
    setSelectedEntity({});
    setIsFormModalOpen(true);
  }, []);

  const openEditModal = useCallback((entity) => {
    setSelectedEntity(entity);
    setIsFormModalOpen(true);
  }, []);

  const closeFormModal = useCallback(() => {
    setIsFormModalOpen(false);
    setSelectedEntity(null);
  }, []);

  const openDeleteModal = useCallback((entity) => {
    setSelectedEntity(entity);
    setIsDeleteModalOpen(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
    setSelectedEntity(null);
  }, []);
  
  const resetOperation = useCallback(() => {
    setIsOperationModalOpen(false);
    setOperationStatus(OPERATION_STATUS.IDLE);
    setOperationError(null);
    setSelectedEntity(null);
    setCurrentOperationType(null);
  }, []);

  // --- LÓGICA DE OPERACIONES CRUD ---
  const executeOperation = useCallback(async (operation, ...args) => {
    setIsOperationModalOpen(true);
    setOperationStatus(OPERATION_STATUS.LOADING);
    setOperationError(null);

    const startTime = Date.now();
    const result = await operation(...args);
    const elapsedTime = Date.now() - startTime;
    const remainingTime = Math.max(0, UX_TIMING.LOADING_MIN_TIME - elapsedTime);

    if (remainingTime > 0) {
      await new Promise(resolve => setTimeout(resolve, remainingTime));
    }

    if (result.success) {
      setOperationStatus(OPERATION_STATUS.SUCCESS);
      setTimeout(resetOperation, UX_TIMING.SUCCESS_DISPLAY_TIME);
    } else {
      setOperationStatus(OPERATION_STATUS.ERROR);
      setOperationError(result.message || `Error al procesar ${entityName}.`);
      setTimeout(resetOperation, UX_TIMING.ERROR_DISPLAY_TIME);
    }
  }, [entityName, resetOperation]);

  const handleFormSubmit = useCallback(async (formData, entityId) => {
    closeFormModal();
    const type = isEditing ? 'update' : 'create';
    setCurrentOperationType(type);
    const operation = crudFunctions[type];
    const args = isEditing ? [entityId, formData] : [formData];
    await executeOperation(operation, ...args);
  }, [isEditing, crudFunctions, closeFormModal, executeOperation]);
  
  const handleDeleteConfirm = useCallback(async () => {
    if (selectedEntity) {
      setCurrentOperationType('delete');
      closeDeleteModal();
      await executeOperation(crudFunctions.delete, selectedEntity[entityIdKey], selectedEntity);
    }
  }, [selectedEntity, crudFunctions, closeDeleteModal, executeOperation, entityIdKey]);

  // --- COMPONENTES DE MODAL ---
  const FormModal = () => (
    <FormModalComponent
      open={isFormModalOpen}
      onClose={closeFormModal}
      onSubmit={(formData) => handleFormSubmit(formData, selectedEntity?.[entityIdKey])}
      entity={selectedEntity}
      isEditing={isEditing}
      {...formModalProps}
    />
  );

  const DeleteModal = () => (
    <ConfirmationModal
      open={isDeleteModalOpen}
      status="confirm"
      title={`¿Eliminar ${entityName}?`}
      subtitle={`¿Estás seguro de que deseas eliminar "${selectedEntity?.nombre || selectedEntity?.name || ''}"? Esta acción no se puede deshacer.`}
      confirmText="Eliminar"
      cancelText="Cancelar"
      onConfirm={handleDeleteConfirm}
      onCancel={closeDeleteModal}
      isDangerous={true}
      userName={selectedEntity?.nombre || selectedEntity?.name || ''}
    />
  );
  
  const getOperationTitle = () => {
    switch (operationStatus) {
      case OPERATION_STATUS.LOADING:
        if (currentOperationType === 'create') return `Creando ${entityName}...`;
        if (currentOperationType === 'update') return `Actualizando ${entityName}...`;
        if (currentOperationType === 'delete') return `Eliminando ${entityName}...`;
        return `Procesando...`;
      
      case OPERATION_STATUS.SUCCESS:
        if (currentOperationType === 'create') return `¡${entityName} Creado!`;
        if (currentOperationType === 'update') return `¡${entityName} Actualizado!`;
        if (currentOperationType === 'delete') return `¡${entityName} Eliminado!`;
        return `¡Éxito!`;
        
      case OPERATION_STATUS.ERROR:
        if (currentOperationType === 'create') return `Error al Crear ${entityName}`;
        if (currentOperationType === 'update') return `Error al Actualizar ${entityName}`;
        if (currentOperationType === 'delete') return `Error al Eliminar ${entityName}`;
        return `Error en la operación`;
        
      default:
        return '';
    }
  };

  const OperationModal = () => (
    <ConfirmationModal
      open={isOperationModalOpen}
      status={operationStatus}
      title={getOperationTitle()}
      subtitle={
        operationStatus === OPERATION_STATUS.LOADING
          ? `Por favor, espera mientras se procesa la solicitud...`
          : operationStatus === OPERATION_STATUS.SUCCESS
            ? `El ${entityName} "${selectedEntity?.nombre || selectedEntity?.name}" ha sido procesado exitosamente.`
            : operationError
      }
      userName={selectedEntity?.nombre || selectedEntity?.name || ''}
      hideActions={true}
    />
  );

  return {
    openCreateModal,
    openEditModal,
    openDeleteModal,
    FormModal, 
    DeleteModal,
    OperationModal,
  };
};

export default usePageOperations; 